import styled from 'styled-components'
import { NavLink } from 'react-router-dom'

import { breakpoints } from '../../assets/styles/mediaBreakpoints'
import { useBool } from '../../hooks/bool'
import { PillButton } from '../common/stand_alone/pillButton'

/**
 * Hamburger component when the screen size is smaller than desktop.
 *
 * @param links - an array of objects with the link and title of the navbar items
 * @param buttons - an array of objects with the link and title of the buttons
 */

type Props = {
  links: { to: string; title: string }[]
  buttons?: { title: string; link: string; width: string }[]
}

const Hamburger = ({ links, buttons }: Props) => {
  const isToggled = useBool(false)
  const logoNode = (
    <NavLink to="/" onClick={() => isToggled.setFalse()}>
      <NavLogo src="/images/logos/full-logo.png" />
    </NavLink>
  )
  return (
    <>
      <NavContainer>
        {logoNode}
        <HamburgerContainer
          onClick={() => isToggled.toggle()}
          isToggled={isToggled.val}
        >
          <HamburgerLine />
          <HamburgerLine />
          <HamburgerLine />
        </HamburgerContainer>
      </NavContainer>
      {isToggled && (
        <SideMenu isToggled={isToggled.val}>
          {logoNode}
          <LinksContainer>
            {links.map((link, index) => (
              <NavItem
                key={index}
                to={link.to}
                onClick={() => isToggled.setFalse()}
              >
                {link.title}
              </NavItem>
            ))}
          </LinksContainer>
          <LineBreak />
          {buttons &&
            buttons.map((button, index) => (
              <PillButton
                key={index}
                link={button.link}
                width={button.width}
                title={button.title}
                onClick={() => isToggled.setFalse()}
              />
            ))}
        </SideMenu>
      )}
    </>
  )
}

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  padding: 1rem;
  @media (min-width: ${breakpoints.mobile + 1}px) {
    padding: 1.5rem;
  }
  @media (min-width: ${breakpoints.desktop + 1}px) {
    display: none;
  }
`
const NavLogo = styled.img`
  height: 40px;
`

const NavItem = styled(NavLink)`
  text-decoration: none;
  margin: 0 1rem;
  font-weight: bold;
  font-size: 1.2rem;
  color: black;
`
const HamburgerLine = styled.span`
  width: 100%;
  height: 2px;
  position: absolute;
  background-color: black;
  border-radius: 2px;
  top: 0;
  left: 0;
  transition: 0.3s;
`

const HamburgerContainer = styled.div<{ isToggled: boolean }>`
  position: relative;
  cursor: pointer;
  width: 24px;
  height: 24px;
  z-index: 999;

  ${HamburgerLine}:nth-child(1) {
    top: ${({ isToggled }) => (isToggled ? '50%' : '25%')};
    transform: ${({ isToggled }) =>
      isToggled ? 'rotate(45deg) translateY(-50%)' : 'rotate(0) translateY(0)'};
    transition: 0.3s;
  }

  ${HamburgerLine}:nth-child(2) {
    top: 50%;
    opacity: ${({ isToggled }) => (isToggled ? '0' : '1')};
    transition: opacity 0.3s;
  }

  ${HamburgerLine}:nth-child(3) {
    top: ${({ isToggled }) => (isToggled ? '50%' : '75%')};
    transform: ${({ isToggled }) =>
      isToggled
        ? 'rotate(-45deg) translateY(-50%)'
        : 'rotate(0) translateY(0)'};
    transition: 0.3s;
  }
`

const SideMenu = styled.div<{ isToggled: boolean }>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  background-color: white;
  padding: 1rem;
  transition: 0.2s ease-in-out;
  transform: translateX(${({ isToggled }) => (isToggled ? '0' : '100%')});
`

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`

const LineBreak = styled.hr`
  width: 100%;
  margin: 2rem 0;
  border-width: 0.5px;
`

export default Hamburger
